import { StringUtils } from '../../utils';
import { gql, useLazyQuery } from '@apollo/client';
import { Card as AntdCard, Button, Col, Form, Input, Row, Spin, Typography, notification } from 'antd';
import Title from 'antd/es/typography/Title';
import React, { JSX, useEffect, useState } from 'react';
import styled from 'styled-components';
import useDocumentTitle from '~/hooks/useDocumentTitle';
import { DOWNLOAD_ETOURS} from '../../routes';
import DownloadError from './DownloadError';
import { DownloadWrapper } from './DownloadWrapper';

const { Text } = Typography;

export interface DownloadAuthProps {
  disseminationKey: string;
  missingEToursOnly: string;
}

const AUTHENTICATE_GQL = gql`
query Authenticate($where: OrderWhereInput) {
  authenticate(where: $where) {
    access_token
    token_type
    expires_in
    scope
  }
}
`;

const AUTHENTICATE_TOUR_GQL = gql`
query AuthenticateTour($where: WellIdentifiersOnOrdersWhereInput) {
  authenticateTour(where: $where) {
    access_token
    token_type
    expires_in
    scope
  }
}
`;

const Card = styled(AntdCard)`
  margin-top: 24px;
  box-shadow: 0 4px 6px -1px #0000001a, 0 2px 4px -2px #0000001a;
`;

const DownloadAuth = (props: DownloadAuthProps): JSX.Element => {
  const [notify, contextHolder] = notification.useNotification();
  const {disseminationKey, missingEToursOnly } = props;
  const [isNoData, setIsNoData] = useState<string>();
  const [mimicIsLoading, setMimicIsLoading] = useState(false);

  const [authenticate, { loading: authLoading, data: authData, error: authError },] = useLazyQuery(missingEToursOnly === "true" ? AUTHENTICATE_TOUR_GQL : AUTHENTICATE_GQL);
  const [form] = Form.useForm();

  console.log({missingEToursOnly})

  useDocumentTitle("Tour Report Downloads Authentication")

  useEffect(() => {
    if (StringUtils.isEmpty(disseminationKey)) {
      setIsNoData("400-001");
      return;
    }

    return () => {
      setIsNoData(null);
    };
  }, []);

  useEffect(() => {
    if (authData == null) {
      return;
    }

    const authenticate = missingEToursOnly === "true" ? authData.authenticateTour : authData.authenticate;
    if (authenticate == null) {
      notify.error({
        message: "Authentication failed!",
        description: "Invalid information provided. Please verify the information provided and try again.",
      });
    } else {
      setMimicIsLoading(true);
      window.sessionStorage.setItem("oauth", JSON.stringify(authenticate));
       
      window.setTimeout(() => {
        const url = "/tours" + DOWNLOAD_ETOURS + disseminationKey+`?missingEToursOnly=${missingEToursOnly}`;
        window.location.href = url;
      }, 300);
    }
  }, [authData]);

  useEffect(() => {
    const err = authError
    if (err != null) {
      notify.error({
        message: "Error!",
        description: err.message ?? "Unknown error.",
      });
    }
  }, [authError]);

  function formFinishHandler(values: any): void {
    const { order, email } = values;
    const where = missingEToursOnly === 'true'
      ? {
        DisseminationKey: { equals: disseminationKey },
        Order: {
          is: {
            AND: [
              {
                Number: { "equals": Number(order) },
              }, {
                ContactEmail: { equals: email }
              }
            ]
          }
        }
      }
      : {
        AND: [
          {
            Number: { equals: Number(order) }
          }, {
            ContactEmail: { equals: email }
          }, {
            DisseminationKey: { equals: disseminationKey }
          },
        ]
      };
    authenticate({
      variables: {
        where,
      },
    });
  }

  const isLoading = authLoading || mimicIsLoading;
  const isShowData = isNoData == null;

  return (
    <Spin spinning={isLoading} tip="Loading ..." size="large">
      <DownloadWrapper>
        {contextHolder}
        <Title level={2} style={{ textAlign: "center", margin: "0 0 0.5rem" }}>Download Tour Reports</Title>
        {isNoData != null && <DownloadError missingEToursOnly={missingEToursOnly} msg={isNoData} />}
        {isShowData && (
          <div>
            <Row gutter={{ lg: 6 }}>
              <Col span={24} lg={6}>&nbsp;</Col>
              <Col span={24} lg={12}>
                <Card>
                  <p style={{ marginBottom: "1.5rem" }}><Text style={{ fontSize: "110%" }}>Please enter the information associated with your order to download the Tour Reports.</Text></p>
                  <Form form={form} layout="vertical" autoComplete="off" onFinish={formFinishHandler}>
                    <Row gutter={{ lg: 6 }}>
                      <Col span={24} lg={23}>
                        <Form.Item name="order" label="Order Number" rules={[{ required: true }, { pattern: /\d+/, message: "Invalid Order Number" }]}>
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={{ lg: 6 }}>
                      <Col span={24} lg={23}>
                        <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={{ lg: 6 }}>
                      <Col span={24} lg={23}>
                        <Form.Item>
                          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "1rem" }}>
                            <Button type="primary" htmlType="submit" size="large">Authenticate</Button>
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Card>
              </Col>
              <Col span={24} lg={6}>&nbsp;</Col>
            </Row>
          </div>
        )}
      </DownloadWrapper>
    </Spin>
  );
};

export default DownloadAuth;
